import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { EASE } from "@/components/motion";

const FIELD = "w-full border hairline bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#0F5A47] transition-colors";

export default function Login() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = mode === "login"
      ? await login(form.email, form.password)
      : await register(form.name, form.email, form.password, form.phone);
    setBusy(false);
    if (res.ok) navigate("/shop");
    else setError(res.error);
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5 pt-32 pb-16" data-testid="login-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="w-full max-w-md">
        <p className="font-mono-ed text-[11px] uppercase tracking-[0.3em] text-[#0F5A47] mb-3 text-center">Kanan Account</p>
        <h1 className="font-display font-extrabold tracking-tight text-4xl text-center">{mode === "login" ? "Welcome back" : "Create account"}</h1>

        <div className="grid grid-cols-2 border hairline bg-white mt-10">
          <button data-testid="login-tab" onClick={() => { setMode("login"); setError(""); }} className={`py-3 font-mono-ed text-[11px] uppercase tracking-[0.2em] transition-colors ${mode === "login" ? "bg-[#0B0F17] text-white" : "text-slate-500 hover:bg-slate-50"}`}>Sign In</button>
          <button data-testid="register-tab" onClick={() => { setMode("register"); setError(""); }} className={`py-3 font-mono-ed text-[11px] uppercase tracking-[0.2em] transition-colors ${mode === "register" ? "bg-[#0B0F17] text-white" : "text-slate-500 hover:bg-slate-50"}`}>Register</button>
        </div>

        <form onSubmit={submit} className="border border-t-0 hairline bg-white p-8 space-y-4">
          {mode === "register" && (
            <>
              <input data-testid="register-name-input" className={FIELD} placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              <input data-testid="register-phone-input" className={FIELD} placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </>
          )}
          <input data-testid="auth-email-input" className={FIELD} type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          <input data-testid="auth-password-input" className={FIELD} type="password" placeholder="Password (min 6 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} />
          {error && <p className="text-xs text-red-600" data-testid="auth-error">{error}</p>}
          <button data-testid="auth-submit-btn" disabled={busy} className="w-full bg-[#0F5A47] text-white py-4 font-mono-ed text-xs uppercase tracking-[0.25em] hover:bg-[#0A4234] transition-colors active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin" />}
            {mode === "login" ? "Sign In" : "Create Account"}
          </button>
          <div className="flex items-center gap-3 py-1">
            <span className="flex-1 h-px bg-slate-200" />
            <span className="font-mono-ed text-[9px] uppercase tracking-[0.2em] text-slate-400">or</span>
            <span className="flex-1 h-px bg-slate-200" />
          </div>
          <button
            type="button"
            data-testid="google-signin-btn"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + '/shop';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full border border-slate-300 bg-white text-slate-700 py-3.5 rounded-xl font-bold text-sm hover:bg-slate-50 hover:border-slate-400 transition-all active:scale-[0.98] flex items-center justify-center gap-2.5"
          >
            <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </button>
          <p className="text-[11px] text-slate-400 text-center">Guest checkout is always available — no account required. <Link to="/shop" className="text-[#0F5A47] underline underline-offset-2">Continue browsing</Link></p>
        </form>
      </motion.div>
    </div>
  );
}
